/**
 * Journey 總覽任務首頁（TRAVEL-JOURNEY-GUIDED-WORKSPACE-01）。
 * 完成狀態只由已載入的實際資料推導；點擊、略過或停留都不算完成，也不寫入任何儲存。
 * 無法區分「不需要」與「尚未填寫」的項目（住宿、交通、行前清單）一律為選填，不進必要分母。
 */

export const GUIDED_TASK_STATUS = Object.freeze({
  DONE: 'done',
  TODO: 'todo',
  UNKNOWN: 'unknown',
  OPTIONAL_ADDED: 'optional-added',
  OPTIONAL_EMPTY: 'optional-empty',
  ENTRY: 'entry',
});

/** 文案 key 前綴；model 只回傳 key 與參數，由呈現層翻譯。 */
const K = 'travel.journeyWorkspace.guide.task';

const PREPARING = new Set(['DRAFT', 'READY']);
const BASIC_EDIT_ROLES = new Set(['OWNER', 'COORDINATOR']);

function scheduleActivityCount(schedule) {
  if (!schedule) return 0;
  const scheduled = (schedule.days || []).reduce((total, day) => total + (day.activities?.length || 0), 0);
  return scheduled + (schedule.unscheduledActivities?.length || 0);
}

/** 是否為可直達的空白日；與繼續安排連結使用同一條件，計數與目標一致。 */
function isEmptyPlanningDay(day) {
  return Boolean(day?.journeyDayId) && (day.activities?.length || 0) === 0;
}

/** 已排日中尚無活動的天數；未排日活動不會讓任何一天變成「已安排」。 */
function scheduleEmptyDayCount(schedule) {
  return (schedule?.days || []).filter(isEmptyPlanningDay).length;
}

/** 基本資料第一個缺漏欄位（目的地 → 開始日 → 結束日）；都已填寫時為 null，編輯器回到標題。 */
function firstMissingBasicField(journey) {
  if (!journey?.destination) return 'destination';
  if (!journey?.startDate) return 'startDate';
  if (!journey?.endDate) return 'endDate';
  return null;
}

/**
 * 活動任務的規劃頁連結。day ID 一律以字串原樣帶入，不經 Number 轉換。
 * 尚無活動：直達第一天並開啟新增表單；已有活動但有空白日：直達第一個空白日，不自動開表單。
 * schedule 未知、沒有任何日或其他情況維持原本的規劃頁入口。
 */
function activitiesPlanningLink(id, schedule, scheduleKnown, activityCount) {
  const base = `/journeys/${id}/planning`;
  const days = scheduleKnown ? (schedule?.days || []) : [];
  if (days.length === 0) return base;
  if (activityCount === 0) {
    const firstDayId = days[0]?.journeyDayId;
    return firstDayId ? `${base}?day=${encodeURIComponent(String(firstDayId))}&compose=1` : base;
  }
  const emptyDay = days.find(isEmptyPlanningDay);
  return emptyDay ? `${base}?day=${encodeURIComponent(String(emptyDay.journeyDayId))}` : base;
}

/**
 * @param {object} input
 * @param {object} input.journey normalizeJourneyWorkspace 結果
 * @param {object|null} input.schedule normalizePlanningSchedule 結果；null＝尚未取得或讀取失敗
 * @param {boolean} input.scheduleKnown schedule 是否已成功讀取（失敗時不可推論為「沒有活動」）
 * @param {Array} input.accommodations
 * @param {Array} input.transports
 */
export function derivePreparationTasks({ journey, schedule, scheduleKnown, accommodations = [], transports = [] }) {
  const id = String(journey?.journeyId ?? '');
  const basicsDone = Boolean(journey?.destination && journey?.startDate && journey?.endDate);
  const activityCount = scheduleActivityCount(schedule);
  const stayCount = accommodations.length;
  const transportCount = transports.length;
  const logisticsAdded = stayCount + transportCount > 0;

  let activitiesStatus = GUIDED_TASK_STATUS.UNKNOWN;
  if (scheduleKnown) activitiesStatus = activityCount > 0 ? GUIDED_TASK_STATUS.DONE : GUIDED_TASK_STATUS.TODO;
  // 空白日只是選填提示：僅在 schedule 已知且已完成起步時補充，不影響狀態與必要分母。
  const emptyDayCount = activitiesStatus === GUIDED_TASK_STATUS.DONE ? scheduleEmptyDayCount(schedule) : 0;

  return [
    {
      key: 'basics',
      required: true,
      titleKey: `${K}.basics.title`,
      descriptionKey: `${K}.basics.description`,
      status: basicsDone ? GUIDED_TASK_STATUS.DONE : GUIDED_TASK_STATUS.TODO,
      statusText: { key: basicsDone ? `${K}.basics.done` : `${K}.basics.todo` },
      action: BASIC_EDIT_ROLES.has(journey?.requesterRole)
        ? { kind: 'edit-basic', labelKey: `${K}.basics.action`, focusField: firstMissingBasicField(journey) }
        : null,
      unavailableHintKey: `${K}.basics.unavailable`,
    },
    {
      key: 'activities',
      required: true,
      titleKey: `${K}.activities.title`,
      descriptionKey: `${K}.activities.description`,
      status: activitiesStatus,
      statusText: {
        [GUIDED_TASK_STATUS.DONE]: { key: `${K}.activities.done`, count: activityCount },
        [GUIDED_TASK_STATUS.TODO]: { key: `${K}.activities.todo` },
        [GUIDED_TASK_STATUS.UNKNOWN]: { key: `${K}.activities.unknown` },
      }[activitiesStatus],
      hintText: emptyDayCount > 0 ? { key: `${K}.activities.emptyDays`, count: emptyDayCount } : null,
      // 一筆活動即完成起步；不要求每天都有活動，完成後仍保留繼續安排的入口
      action: {
        kind: 'link',
        to: activitiesPlanningLink(id, schedule, scheduleKnown, activityCount),
        labelKey: activitiesStatus === GUIDED_TASK_STATUS.DONE
          ? `${K}.activities.actionContinue`
          : `${K}.activities.actionStart`,
      },
    },
    {
      key: 'logistics',
      required: false,
      titleKey: `${K}.logistics.title`,
      descriptionKey: `${K}.logistics.description`,
      status: logisticsAdded ? GUIDED_TASK_STATUS.OPTIONAL_ADDED : GUIDED_TASK_STATUS.OPTIONAL_EMPTY,
      statusText: logisticsAdded
        ? { key: `${K}.logistics.added`, values: { stays: stayCount, transports: transportCount } }
        : { key: `${K}.logistics.empty` },
      action: { kind: 'link', to: `/journeys/${id}/stays`, labelKey: `${K}.logistics.actionStays` },
      secondaryAction: { kind: 'link', to: `/journeys/${id}/transport`, labelKey: `${K}.logistics.actionTransport` },
    },
    {
      key: 'departure',
      required: false,
      titleKey: `${K}.departure.title`,
      descriptionKey: `${K}.departure.description`,
      status: GUIDED_TASK_STATUS.ENTRY,
      statusText: { key: `${K}.departure.optional` },
      action: { kind: 'link', to: `/journeys/${id}/checklist`, labelKey: `${K}.departure.action` },
    },
  ];
}

/** 必要任務的完成數；UNKNOWN 不算完成。 */
export function preparationProgress(tasks) {
  const required = tasks.filter((task) => task.required);
  const done = required.filter((task) => task.status === GUIDED_TASK_STATUS.DONE).length;
  const hasUnknown = required.some((task) => task.status === GUIDED_TASK_STATUS.UNKNOWN);
  return {
    done,
    total: required.length,
    complete: required.length > 0 && done === required.length,
    hasUnknown,
    textKey: 'travel.journeyWorkspace.guide.prepare.progress',
  };
}

/** 一次只突出一項：第一個未完成的必要任務 → 尚未加入的選填住宿交通 → 出發準備。 */
export function primaryPreparationTask(tasks) {
  return tasks.find((task) => task.required && task.status !== GUIDED_TASK_STATUS.DONE)
    || tasks.find((task) => task.status === GUIDED_TASK_STATUS.OPTIONAL_EMPTY)
    || tasks.find((task) => task.key === 'departure');
}

/**
 * 依 lifecycle 決定首頁模式。
 * prepare：規劃中／準備出發；traveling：今日與記錄；completed：整理與分享；closed：取消／封存不催促。
 */
export function guidedWorkspaceMode(lifecycleStatus) {
  if (PREPARING.has(lifecycleStatus)) return 'prepare';
  if (lifecycleStatus === 'TRAVELING') return 'traveling';
  if (lifecycleStatus === 'COMPLETED') return 'completed';
  if (lifecycleStatus === 'CANCELLED' || lifecycleStatus === 'ARCHIVED') return 'closed';
  return 'unknown';
}
